import { useEffect } from "react";

import {
  Outlet,
  createRootRouteWithContext,
  redirect,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import {
  bffDocumentRedirect,
  buildOidcLoginUrlForPath,
  clearAuthRedirectFlag,
  markLoginRedirecting,
} from "@/lib/api/auth-redirect";
import { ensureAuthConfig } from "@/lib/api/auth-config";
import { AuthStatusCheckView } from "@/components/common/AuthStatusCheckView";
import { MatomoSessionBridge } from "@/components/common/MatomoSessionBridge";
import { MobileUnsupportedOverlay } from "@/components/common/MobileUnsupportedOverlay";
import { RootErrorView } from "@/components/common/RootErrorView";
import { MatomoConsentUiProvider } from "@/contexts/MatomoConsentUiContext";
import { cguConfigQueryOptions } from "@/lib/queries/cgu-config";
import {
  currentUserQueryOptions,
  isCurrentUserBetaAccessDenied,
  isCurrentUserUnauthorized,
} from "@/lib/queries/current-user";
import { transitionFromAppSplash } from "@/lib/ui-helper/app-splash";

const RootComponent = () => {
  useEffect(() => {
    transitionFromAppSplash();
  }, []);

  return (
    <MatomoConsentUiProvider>
      <MobileUnsupportedOverlay />
      <MatomoSessionBridge />
      <Outlet />
    </MatomoConsentUiProvider>
  );
};
export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  beforeLoad: async ({ location, context }) => {
    if (typeof window === "undefined") {
      return;
    }

    await ensureAuthConfig();

    if (
      location.pathname.startsWith("/auth/") ||
      location.pathname.startsWith("/share/") ||
      location.pathname === "/forbidden" ||
      location.pathname === "/beta-closed"
    ) {
      return;
    }

    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const loginHref = buildOidcLoginUrlForPath(returnTo);

    try {
      await context.queryClient.ensureQueryData(currentUserQueryOptions());
      clearAuthRedirectFlag();
      await context.queryClient.ensureQueryData(cguConfigQueryOptions());
    } catch (error) {
      if (isCurrentUserBetaAccessDenied(error)) {
        throw redirect({ to: "/beta-closed" });
      }
      if (isCurrentUserUnauthorized(error)) {
        markLoginRedirecting();
        throw redirect(bffDocumentRedirect(loginHref));
      }
      // Indisponibilité plateforme (ou tout autre échec hors auth/bêta) → RootErrorView
      throw error;
    }
  },
  component: RootComponent,
  errorComponent: RootErrorView,
  pendingComponent: AuthStatusCheckView,
  pendingMs: 0,
  pendingMinMs: 0,
});
